import { z } from 'zod';
import { stampedBody } from '../common/row-version.js';
import { AssetCategorySchema, RatingUnitSchema } from './categories.js';
import { DEVICE_ACCESS_FIELDS, DEVICE_ACCESS_KINDS, DeviceAccessKindSchema, IpAddressSchema, SimSlotSchema } from './access.js';
import { AssetConditionGradeSchema, AssetEventReasonTypeSchema } from './events.js';
import { ImportFormatSchema } from '../import-export/envelopes.js';
import { LanguageSchema } from '../common/language.js';
import { CurrencyCodeSchema } from '../common/currency.js';
import { AdjustAssetInputSchema, AssetLocationSchema, DeclareLostInputSchema, DisposeAssetInputSchema, EditAssetInputSchema, MoveAssetInputSchema, PutOnHoldInputSchema, ReactivateAssetInputSchema, RecoverAssetInputSchema, ReinstateAssetInputSchema, ReplaceAssetInputSchema, RetireAssetInputSchema, TransferOwnershipInputSchema, WriteOffAssetInputSchema, } from './verbs.js';
import { PaginationSchema } from '../common/pagination.js';
// Register wire requests. The VERB INPUT SHAPES are locked in verbs.ts
// (ADR-063 — one verb, many callers); the route bodies are those shapes minus
// `assetId`, which rides the URL. CreateAssetInputSchema is used as the POST
// body verbatim (no id yet).
export const AssetStatusSchema = z.enum(['stocked', 'deployed', 'on_hold', 'retired', 'lost']);
export const MoveAssetBodySchema = MoveAssetInputSchema.omit({ assetId: true });
export const ReplaceAssetBodySchema = ReplaceAssetInputSchema.omit({ outgoingAssetId: true });
export const AdjustAssetBodySchema = AdjustAssetInputSchema.omit({ assetId: true });
export const EditAssetBodySchema = EditAssetInputSchema.omit({ assetId: true }).extend(stampedBody);
export const DeclareLostBodySchema = DeclareLostInputSchema.omit({ assetId: true });
export const RecoverAssetBodySchema = RecoverAssetInputSchema.omit({ assetId: true });
export const ReactivateAssetBodySchema = ReactivateAssetInputSchema.omit({ assetId: true });
export const ReinstateAssetBodySchema = ReinstateAssetInputSchema.omit({ assetId: true });
export const WriteOffAssetBodySchema = WriteOffAssetInputSchema.omit({ assetId: true });
export const DisposeAssetBodySchema = DisposeAssetInputSchema.omit({ assetId: true });
export const TransferOwnershipBodySchema = TransferOwnershipInputSchema.omit({ assetId: true });
// The two door COMPOSITIONS (ADR-063 doors ≠ verbs: an optional body block
// on the verb's own route, never a dedicated door endpoint). The route
// handler runs both verbs in ONE transaction under ONE correlation; each
// rung keeps its own gate, event row, and audit record.
// retire + dispose — "and it's already physically gone" (Decommission /
// Declare-faulty's Dispose-now). The rung shares the verdict's effectiveOn:
// one act, one day.
export const RetireAssetBodySchema = RetireAssetInputSchema.omit({ assetId: true }).extend({
    dispose: z
        .object({
        method: z.enum(['scrapped', 'sold', 'donated', 'returned']),
        reasonType: AssetEventReasonTypeSchema.optional(),
        note: z.string().trim().min(1).max(2000).optional(),
    })
        .strict()
        .optional(),
});
// putOnHold + move — "quarantine and pull it to a warehouse" (Declare-faulty's
// pull). putOnHold is in-place by design, so the pull composes a `moved` row
// (resulting on_hold) after the flip — the finding stays on the leading row.
export const PutOnHoldBodySchema = PutOnHoldInputSchema.omit({ assetId: true }).extend({
    pullTo: AssetLocationSchema.optional(),
    // with the pull: "Move its parts too" (ticked by default — the move verb's tick)
    includeChildren: z.boolean().default(true),
});
// The correction door (the ONE effective-date repair — the clocks law): only
// the day + the event's own note may move; axes are corrected by reversing
// acts, never edits. Gate + slide bounds are service-side (per event family).
export const CorrectEventDateBodySchema = z
    .object({
    ...stampedBody,
    effectiveOn: z.string().trim().regex(/^\d{4}-\d{2}-\d{2}$/, 'YYYY-MM-DD'),
    note: z.string().trim().min(1).max(2000).nullable().optional(), // null clears; undefined untouched
})
    .strict();
// Register list (offset, 100 a page — CONVENTIONS §Pagination: a register,
// bounded and counted exactly; random-access filter UX, the sites precedent).
// Two FACES over one collection (register = ops, books = finance — the books
// face is gated asset.books:read at the route and adds the books sub-object per
// row) and the Devices entry's own population, the sites sort grammar (sort
// allowlisted per face), the strip's kind lens (`queue`) beside the family
// filter, and grouping as BANDS (ADR-078 §4): every band of the filtered
// register with its true count, plus the rows of the bands the person opened.
// canonical home: common/query.ts (promoted on the third consumer).
import { csv as csvOf } from '../common/query.js';
import { LocationParamSchema } from '../common/location.js';
import { TerritoryParamSchema } from '../common/territory.js';
import { BandKeySchema } from '../common/bands.js';
// register/books = the two column projections over one route (?view=);
// devices = the Devices ENTRY's read (the face-vs-entry law: same
// machinery, its own population): implies the capable population server-side,
// serves the per-row connectivity block + the five family counts, and stays
// on plain `asset:read` (the F6 law — the address half is member-visible).
export const AssetListFaceSchema = z.enum(['register', 'books', 'devices']);
// Sortable columns = clear-criterion only (batch-1): reference (the identity
// column), the location label; the books face adds book_status + acquired_on.
export const AssetListSortSchema = z.enum([
    'default', 'product', 'reference', 'reference_alt', 'location', 'status', 'created',
    'rating', 'condition', 'warranty', 'book_status', 'cost', 'owner',
]);
// The group axes of the three faces, in the gear's order — each small enough to send whole (ADR-078 §4): a unit's
// category · its product (`model`) · the kind of place it sits at · its zone · its cluster (the site's it is at, or
// whose storage holds it; a depot or a vehicle belongs to none — one last band). A place and a gateway are
// thousands-valued, never axes: a place is a filter (`location`), a gateway is reached by its site or the search.
export const ASSET_GROUP_BYS = ['category', 'model', 'location_type', 'zone', 'cluster'];
export const AssetListGroupSchema = z.enum(ASSET_GROUP_BYS);
/** A location-type band's keys, in the band list's order — where a unit sits (`none` = nowhere, last). */
export const ASSET_LOCATION_TYPES = ['site', 'site_storage', 'depot', 'vehicle_stock'];
export const BookStatusSchema = z.enum(['on_books', 'written_off', 'disposed']);
export const AcquisitionModeSchema = z.enum(['purchased', 'leased', 'customer_provided', 'other']);
// Warranty is a DERIVED state of warranty_end_on (active ≥ today · expired <
// today · none = null) — a filter word, never a stored column.
export const WarrantyStateSchema = z.enum(['active', 'expired', 'none']);
export const AssetListQuerySchema = PaginationSchema.extend({
    q: z.string().trim().max(64).optional(),
    face: AssetListFaceSchema.default('register'),
    category: csvOf(AssetCategorySchema),
    status: csvOf(AssetStatusSchema),
    site_id: z.string().trim().min(1).max(16).optional(),
    warehouse_id: z.string().trim().min(1).max(16).optional(),
    model_id: z.string().trim().min(1).max(16).optional(),
    parent_id: z.string().trim().min(1).max(16).optional(),
    // Allocation lens (batch-2 — replaces the unallocated bool): mixed rows are
    // the DEFAULT (ADR-066 §5); 'pool' narrows to Unallocated, 'project' to
    // this project's own rows.
    allocation: z.enum(['pool', 'project']).optional(),
    // The Location filter (ADR-037 §2 — kinded tokens, picks add up): `site:<id>` = everything AT the site, installed
    // or in its storage · `depot:<id>` · `vehicle:<id>` (its stock hold) · `<kind>:*` = the whole kind.
    location: LocationParamSchema,
    // The territory (common/territory.ts — the sites list's own kinded param): `zone:<id>` · `cluster:<id>`, the picks
    // add up — the units at a site of it, installed or in its storage.
    territory: TerritoryParamSchema,
    // Resolved-capability lens (`model.is_network_device ?? the charter three-
    // state`) — the Via picker's population narrows on it server-side.
    capable: z.literal('true').optional(),
    // The Devices entry's Connectivity filter group (face=devices; inert
    // elsewhere — the Devices-entry filter ruling (2026-08-31)). `credential_state` is status-BLIND by design
    // (find the stocked secretless router before it deploys).
    path: csvOf(z.enum(['direct', 'via', 'none'])),
    service_kind: csvOf(DeviceAccessKindSchema),
    credential_state: csvOf(z.enum(['complete', 'missing', 'none_required'])),
    // The strip's kind lens (overlapping counts) + the '+ Filter' precision axes.
    queue: csvOf(z.enum(['missing', 'conflict', 'unverified'])),
    observation: csvOf(z.string().trim().min(1).max(64)), // family keys, service-validated against the registry
    impact: csvOf(z.enum(['high', 'medium', 'low'])), // register face only (batch-1)
    condition: csvOf(z.enum(['good', 'fair', 'poor', 'failed'])), // the row verdict
    warranty: csvOf(WarrantyStateSchema), // about the unit — every face
    // The money record's filters — they act on the books face alone (the route's books gate covers the face).
    book_status: csvOf(BookStatusSchema),
    acquisition_mode: csvOf(AcquisitionModeSchema),
    owner_id: csvOf(z.string().trim().min(1).max(16)),
    sort_by: AssetListSortSchema.default('default'),
    sort_dir: z.enum(['asc', 'desc']).default('asc'),
    group_by: AssetListGroupSchema.optional(),
    // The STREAM's open set (with `group_by`): the keys of the bands the person opened, `*` = every band; absent =
    // folded — the bands and the strip's counts, no rows (how a grouped view arrives).
    open: csvOf(BandKeySchema),
}).strict();
// ── import/export datasets (ADR-041 on the ADR-063 verbs) ────────────────────
// One register import row — THE DECLARATIVE LIFECYCLE: a row states what is
// true now (status · place · facts · the story of the change) and the column
// model derives the verb from the delta against the stored row. The match key
// is `reference` (the customer unit tag; SCHEMA §assets "the import match key
// when present"; `reference_alt` is the fallback lookup — serials are evidence,
// never match keys). Everything except `reference` is optional at the schema
// layer: an absent column / empty cell = untouched on updates; create-required
// facts (category · status · a place unless retired/lost) are enforced by the
// column model per row so the requirement is dry-run-visible.
//
// Location grammar: `location_type` ∈ site · site_storage · depot · vehicle +
// `location` = the place's REFERENCE (site code · site code · depot code ·
// vehicle plate). The type may stay blank when the value resolves uniquely and
// the status disambiguates (stocked at a site reference = its storage).
// The story trio (`effective_on` · `reason` · `note`) rides the change the row
// makes; on a row that changes nothing it is ignored with a counted warning.
const dateCell = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'expected YYYY-MM-DD');
export const ASSET_IMPORT_LOCATION_TYPES = ['site', 'site_storage', 'depot', 'vehicle'];
export const AssetImportRowSchema = z
    .object({
    reference: z.string().trim().min(1).max(48),
    reference_alt: z.string().trim().min(1).max(48).optional(),
    category: AssetCategorySchema.optional(),
    // The product cell — resolved CI against `manufacturer + " " + name`, bare
    // unambiguous `name`, or `part_number`; unmatched values enumerate in the
    // preview's pick block (refPicks) and map via the run's model_map.
    model: z.string().trim().min(1).max(128).optional(),
    serial_number: z.string().trim().min(1).max(64).optional(),
    name: z.string().trim().min(1).max(64).optional(),
    parent: z.string().trim().min(1).max(48).optional(), // parent asset reference (same project or earlier row)
    // ── place & status (the lifecycle axes) ──
    status: AssetStatusSchema.optional(),
    location_type: z.enum(ASSET_IMPORT_LOCATION_TYPES).optional(),
    location: z.string().trim().min(1).max(64).optional(), // the place's reference
    // ── the story of the change ──
    effective_on: dateCell.optional(), // the day the change took effect; blank = today
    reason: AssetEventReasonTypeSchema.optional(),
    note: z.string().trim().min(1).max(2000).optional(),
    // ── technical ──
    unit_count: z.number().int().positive().optional(), // aggregate categories only
    rating_value: z.number().positive().optional(), // generic-model assets only (ADR-065 rung 2)
    rating_unit: RatingUnitSchema.optional(),
    condition: AssetConditionGradeSchema.optional(), // the 4-grade verdict — rides a moment's axis, or an edit alone
    state_of_health_pct: z.number().min(0).max(100).optional(), // battery categories
    device_ip: IpAddressSchema.optional(),
    via: z.string().trim().min(1).max(48).optional(), // the fronting unit's reference (the addressing edge)
    reserved_for_site: z.string().trim().min(1).max(32).optional(), // site reference (stocked units)
    // ── books (asset.books:write — rejected per row without the gate) ──
    owner: z.string().trim().min(1).max(64).optional(), // an attached owner's name
    acquired_on: dateCell.optional(),
    acquired_condition: z.enum(['new', 'used']).optional(),
    acquisition_cost: z.number().positive().optional(),
    acquisition_currency: CurrencyCodeSchema.optional(), // a code the runtime knows (common/currency); the verb judges the cost's decimals against it
    acquisition_mode: z.enum(['purchased', 'leased', 'customer_provided', 'other']).optional(),
    supplier: z.string().trim().min(1).max(64).optional(),
    warranty_end_on: dateCell.optional(),
    disposal: z.enum(['scrapped', 'sold', 'donated', 'returned']).optional(), // filled on a retired row = the dispose rung
})
    .strict();
// ── the DEVICES dataset — the connectivity facet's file twin (ADR-063 §access
//    facet: secrets export-blind, import-transient). One row per device (an
//    EXISTING register row, matched on reference): the two reach facts
//    (address · reached-through), one column group per service kind built
//    from the access registry (`<kind>_<field>` — ports · usernames · schemes
//    · unit ids · the SECRET fields, which the engine's `secret` cell never
//    echoes), and the SIM slots as positional groups (`sim1_*` · `sim2_*`; the
//    group IS the slot — slots 3–4 are recognized, not offered). Blank = keep
//    (a secret is never cleared by a file). ────────────────────────────────────
export const DEVICE_SHEET_KINDS = DEVICE_ACCESS_KINDS.filter((k) => k !== 'cellular');
export const DEVICE_SHEET_SIM_SLOTS = [1, 2, 3, 4];
export const DEVICE_SHEET_SIM_FIELDS = ['imsi', 'msisdn', 'apn_ip', 'apn_ip_backup'];
export const deviceSheetKey = (kind, field) => `${kind}_${field}`;
export const simSheetKey = (slot, field) => `sim${slot}_${field}`;
function deviceCellSchema(f) {
    switch (f.kind) {
        case 'port':
            return z.number().int().min(1).max(65535);
        case 'int':
            return z.number().int().min(f.min ?? 0).max(f.max ?? 2147483647);
        case 'enum':
            return z.enum(f.options);
        case 'secret':
            return z.string().min(1).max(255);
        case 'ip':
            return IpAddressSchema;
        default:
            return z.string().trim().min(1).max(64);
    }
}
const deviceRowShape = {
    reference: z.string().trim().min(1).max(48),
    device_ip: IpAddressSchema.optional(),
    via: z.string().trim().min(1).max(48).optional(), // the fronting unit's reference; the clear token '-' drops the edge
};
for (const kind of DEVICE_SHEET_KINDS) {
    for (const f of DEVICE_ACCESS_FIELDS[kind])
        deviceRowShape[deviceSheetKey(kind, f.key)] = deviceCellSchema(f).optional();
}
for (const slot of DEVICE_SHEET_SIM_SLOTS) {
    deviceRowShape[simSheetKey(slot, 'imsi')] = SimSlotSchema.shape.imsi;
    deviceRowShape[simSheetKey(slot, 'msisdn')] = SimSlotSchema.shape.msisdn;
    deviceRowShape[simSheetKey(slot, 'apn_ip')] = SimSlotSchema.shape.apn_ip;
    deviceRowShape[simSheetKey(slot, 'apn_ip_backup')] = SimSlotSchema.shape.apn_ip_backup;
}
export const DeviceImportRowSchema = z.object(deviceRowShape).strict();
// The manual product-code mapping (A4: pick at preview; the per-project overlay
// table stays named-and-deferred). Rides the import query string JSON-encoded;
// the service parses + re-validates against the catalog (existence + category
// legality per row) — a stale/foreign id is a row error, never trusted.
export const AssetModelMapSchema = z
    .array(z
    .object({
    value: z.string().trim().min(1).max(128), // the file's model cell, verbatim
    id: z.string().trim().min(1).max(16), // asset_models id picked by the user
})
    .strict())
    .max(60);
// An asset FILE — the screen's own question as ONE localized data sheet: the list query less the page and the bands
// (`face` names the screen the question was asked on — its lens and its family filter resolve there; the SHEET, the
// route, names the columns: a Devices sheet asked from the register holds the devices among the register's units).
// Books / device columns ride the caller's gates (asset.books:read · asset.private_fields:read).
export const ExportAssetsQuerySchema = AssetListQuerySchema.omit({
    limit: true,
    offset: true,
    group_by: true,
    open: true,
})
    .extend({
    format: ImportFormatSchema.default('xlsx'),
    lang: LanguageSchema.default('en'),
})
    .strict();
// The assets family's ONE-workbook export (ADR-059 combined workbooks): the
// selected datasets as sheets of one XLSX — the Register (the view's face),
// the Devices file, the project's local Products. The union view rides the
// query; each sheet applies its own subset (the products sheet reads only
// `category`), and the cover says per sheet what it applied.
export const ASSETS_COMBINED_DATASETS = ['register', 'devices', 'products'];
export const AssetsCombinedDatasetSchema = z.enum(ASSETS_COMBINED_DATASETS);
export const ExportAssetsCombinedQuerySchema = ExportAssetsQuerySchema.omit({ format: true })
    .extend({
    datasets: z.preprocess((v) => (typeof v === 'string' ? v.split(',').map((s) => s.trim()).filter(Boolean) : v), z.array(AssetsCombinedDatasetSchema).min(1).max(3)),
})
    .strict();
